import { Link } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';

import { AdminLayout } from '@/app/shell/admin-layout';
import { type EmailStatus, type ListEmailsQuery, useAdminEmails } from '@/shared/api/admin-emails';
import { cn } from '@/shared/lib/cn';

const PAGE_SIZE = 100;
const STATUSES: EmailStatus[] = ['queued', 'sent', 'failed', 'bounced', 'delivered'];

export function EmailLogPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<EmailStatus | undefined>(undefined);
  const [offset, setOffset] = useState(0);

  const query: ListEmailsQuery = { limit: PAGE_SIZE, offset };
  if (search.trim()) query.search = search.trim();
  if (status) query.status = status;

  const { data, isFetching } = useAdminEmails(query);

  // Resend is intentionally not exposed here today — the email_log row only
  // keeps a 200-char body preview, so the server cannot rebuild a faithful
  // envelope to push back through the outbox.  Surface this read-only and
  // bring the button back when a typed re-trigger per template ships.

  return (
    <AdminLayout>
      <header className="mb-4">
        <h1 className="text-xl font-semibold">Email log</h1>
        <p className="text-sm text-neutral-400">
          Outbound emails sent by Projoint.  Read-only — resend is not yet implemented (see service notes).
        </p>
      </header>
      <div className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-2 text-sm text-amber-200">
        This table is not written by the mail pipeline yet, so it stays empty. Sent / failed / queued email counts
        (from the delivery outbox) are on the{' '}
        <Link to="/" className="underline">
          Overview
        </Link>
        .
      </div>

      <div className="mb-4 flex items-center gap-2">
        <div className="relative max-w-md flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOffset(0);
            }}
            placeholder="Recipient email or subject"
            className="w-full rounded-md bg-neutral-900 py-2 pl-9 pr-3 text-sm text-neutral-100 outline-none ring-1 ring-neutral-800 focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <label className="flex items-center gap-2 rounded-md bg-neutral-900 px-2 py-1.5 text-xs ring-1 ring-neutral-800">
          <span className="text-neutral-500">Status:</span>
          <select
            value={status ?? ''}
            onChange={(e) => {
              setStatus((e.target.value || undefined) as EmailStatus | undefined);
              setOffset(0);
            }}
            className="bg-transparent text-neutral-200 outline-none"
          >
            <option value="">all</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <div className="ml-auto text-xs text-neutral-500">
          {isFetching ? 'Loading…' : data ? `${data.total} total` : null}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-2 font-medium">When</th>
              <th className="px-4 py-2 font-medium">Recipient</th>
              <th className="px-4 py-2 font-medium">Template</th>
              <th className="px-4 py-2 font-medium">Subject</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {data?.items.map((e) => (
              <tr key={e.id} className="hover:bg-neutral-800/40">
                <td className="px-4 py-2 text-neutral-400">{formatDateTime(e.createdAt)}</td>
                <td className="px-4 py-2 text-neutral-200">{e.toEmail}</td>
                <td className="px-4 py-2">
                  <code className="rounded bg-neutral-800 px-1.5 py-0.5 text-xs text-orange-400">
                    {e.templateKey}
                  </code>
                </td>
                <td className="max-w-xs truncate px-4 py-2 text-neutral-300" title={e.subject}>
                  {e.subject}
                </td>
                <td className="px-4 py-2">
                  <StatusBadge status={e.status} />
                  {e.failureReason ? (
                    <div className="mt-0.5 text-[10px] text-red-400" title={e.failureReason}>
                      {e.failureReason.slice(0, 40)}…
                    </div>
                  ) : null}
                </td>
                <td className="px-4 py-2 text-right text-xs text-neutral-600">
                  —
                </td>
              </tr>
            ))}
            {data && data.items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-neutral-500">
                  No emails match.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {data && data.total > PAGE_SIZE ? (
        <div className="mt-4 flex items-center justify-between text-sm text-neutral-400">
          <div>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, data.total)} of {data.total}
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="rounded-md bg-neutral-800 px-3 py-1.5 hover:bg-neutral-700 disabled:opacity-40"
            >
              ← Prev
            </button>
            <button
              disabled={offset + PAGE_SIZE >= data.total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="rounded-md bg-neutral-800 px-3 py-1.5 hover:bg-neutral-700 disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        </div>
      ) : null}

    </AdminLayout>
  );
}

function StatusBadge({ status }: { status: EmailStatus }) {
  const tone = {
    queued: 'bg-neutral-700/30 text-neutral-300 ring-neutral-600',
    sent: 'bg-blue-500/15 text-blue-300 ring-blue-500/30',
    delivered: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    failed: 'bg-red-500/15 text-red-300 ring-red-500/30',
    bounced: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
  }[status];
  return (
    <span className={cn('inline-block rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ring-1', tone)}>
      {status}
    </span>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}
